import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import ReactECharts from 'echarts-for-react'
import { ArrowLeft, BookOpen, Cloud, BarChart3, FileText, Tag, Calendar, User, ExternalLink, ChevronRight, Wind } from 'lucide-react'
import { API_BASE } from '../App'

function PaperDetail() {
  const { id } = useParams()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('translation')
  const [vizData, setVizData] = useState({})

  useEffect(() => {
    fetch(`${API_BASE}/api/papers/${id}`)
      .then(r => r.json())
      .then(data => {
        setData(data)
        setLoading(false)
        if (data.visualizations) {
          data.visualizations.forEach(v => {
            if (v.chart_config) {
              try {
                setVizData(prev => ({...prev, [v.id]: JSON.parse(v.chart_config)}))
              } catch (e) {}
            }
          })
        }
      })
      .catch(() => setLoading(false))
  }, [id])

  if (loading) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  )

  if (!data || !data.paper) return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="text-center">
        <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">文献不存在或已被删除</p>
        <Link to="/" className="btn-primary inline-block mt-4">返回首页</Link>
      </div>
    </div>
  )

  const { paper, translations, visualizations, analyses } = data

  const sectionNames = {
    'abstract': '摘要',
    'intro': '引言',
    'methods': '方法',
    'results': '结果',
    'discussion': '讨论',
    'conclusion': '结论'
  }

  const tabs = [
    { id: 'translation', label: '翻译', icon: FileText },
    { id: 'visualization', label: '可视化', icon: BarChart3 },
    { id: 'analysis', label: '分析', icon: Wind }
  ]

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top Nav */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-primary-700 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>返回列表</span>
          </Link>
          <div className="h-6 w-px bg-gray-200"></div>
          <span className="text-sm text-gray-500 truncate">{paper.title_cn || paper.title}</span>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Paper Header Card */}
        <div className="card mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="px-3 py-1 bg-primary-50 text-primary-700 text-sm font-medium rounded-full">
              {paper.category}
            </span>
            <span className="text-gray-400 text-sm flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {paper.year}
            </span>
          </div>

          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
            {paper.title_cn || paper.title}
          </h1>
          <h2 className="text-lg text-gray-500 mb-4">{paper.title}</h2>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600 mb-4">
            <span className="flex items-center gap-1">
              <User className="w-4 h-4 text-gray-400" />
              {paper.authors}
            </span>
            <span>{paper.journal}</span>
            {paper.doi && (
              <a 
                href={`https://doi.org/${paper.doi}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary-600 hover:underline flex items-center gap-1"
              >
                DOI: {paper.doi}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {paper.keywords && (
            <div className="flex flex-wrap gap-2 mb-4">
              {paper.keywords.split(/[,，]/).map((k, i) => (
                <span key={i} className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-md flex items-center gap-1">
                  <Tag className="w-3 h-3" />
                  {k.trim()}
                </span>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            {paper.pdf_aliyun_url && (
              <a 
                href={paper.pdf_aliyun_url}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-orange-50 text-orange-700 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors flex items-center gap-2"
              >
                <Cloud className="w-4 h-4" />
                阿里云盘下载 PDF
              </a>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white p-1 rounded-xl border border-gray-200 shadow-sm">
          {tabs.map(tab => {
            const Icon = tab.icon
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-all font-medium ${
                  activeTab === tab.id 
                    ? 'bg-primary-600 text-white shadow-md' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'translation' && translations.length > 0 && (
                  <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded">{translations.length + 1}</span>
                )}
                {tab.id === 'visualization' && visualizations.length > 0 && (
                  <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded">{visualizations.length}</span>
                )}
                {tab.id === 'analysis' && analyses.length > 0 && (
                  <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded">{analyses.length}</span>
                )}
              </button>
            )
          })}
        </div>

        {/* Content */}
        <div className="space-y-6">
          {activeTab === 'translation' && (
            <div className="space-y-6">
              <div className="card border-l-4 border-l-primary-500">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary-600" />
                  摘要
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">原文</div>
                    <p className="text-gray-600 text-sm leading-relaxed">{paper.abstract || '暂无摘要'}</p>
                  </div>
                  <div>
                    <div className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-2">中文翻译</div>
                    <p className="text-gray-900 leading-relaxed">{paper.abstract_cn || '暂无翻译'}</p>
                  </div>
                </div>
              </div>

              {translations.map(t => (
                <div key={t.id} className="card border-l-4 border-l-primary-300">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">
                    {sectionNames[t.section] || t.section}
                    {t.translator && (
                      <span className="text-sm font-normal text-gray-400 ml-2">译者: {t.translator}</span>
                    )}
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">原文</div>
                      <p className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">{t.content || '暂无内容'}</p>
                    </div>
                    <div>
                      <div className="text-xs font-semibold text-primary-600 uppercase tracking-wider mb-2">中文翻译</div>
                      <p className="text-gray-900 leading-relaxed whitespace-pre-wrap">{t.content_cn || '暂无翻译'}</p>
                    </div>
                  </div>
                </div>
              ))}

              {translations.length === 0 && !paper.abstract && (
                <div className="card text-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">暂无翻译内容</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'visualization' && (
            <div className="space-y-8">
              {visualizations.map(viz => (
                <div key={viz.id} className="card">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{viz.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{viz.description}</p>
                  {vizData[viz.id] && (
                    <ReactECharts 
                      option={vizData[viz.id]}
                      style={{ height: 450 }}
                      opts={{ renderer: 'canvas' }}
                    />
                  )}
                  {viz.data_source && (
                    <p className="text-xs text-gray-400 mt-3">数据来源: {viz.data_source}</p>
                  )}
                </div>
              ))}
              {visualizations.length === 0 && (
                <div className="card text-center py-16">
                  <BarChart3 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">暂无可视化数据</p>
                  <p className="text-gray-400 text-sm mt-1">可在管理后台添加图表</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'analysis' && (
            <div className="space-y-6">
              {analyses.map(a => (
                <div key={a.id} className="card">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{a.title}</h3>
                  <div 
                    className="prose max-w-none text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{__html: a.content?.replace(/\n/g, '<br/>') || ''}} 
                  />
                  {a.key_findings && (
                    <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <h4 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                        <ChevronRight className="w-4 h-4" />
                        核心发现
                      </h4>
                      <p className="text-amber-900 text-sm leading-relaxed">{a.key_findings}</p>
                    </div>
                  )}
                  {a.meteorological_significance && (
                    <div className="mt-4 bg-primary-50 border border-primary-200 rounded-lg p-4">
                      <h4 className="font-bold text-primary-800 mb-2 flex items-center gap-2">
                        <Wind className="w-4 h-4" />
                        气象学意义
                      </h4>
                      <p className="text-primary-900 text-sm leading-relaxed">{a.meteorological_significance}</p>
                    </div>
                  )}
                </div>
              ))}
              {analyses.length === 0 && (
                <div className="card text-center py-16">
                  <Wind className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">暂无分析文章</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PaperDetail
